import React, { useState, useMemo } from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet } from 'react-native';
import { ciphers } from '../../data/ciphers';
import { useAppTheme } from '../../utils/useAppTheme';

// Get cipher options for the picker
const getCipherOptions = () => {
  const options = [];
  Object.keys(ciphers).forEach(key => {
    const cipher = ciphers[key];
    if (cipher && cipher.name && cipher.values) {
      options.push({ key, name: cipher.name });
    }
  });
  return options;
};

const CipherChartPage = () => {
  const { colors } = useAppTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const [selectedCipher, setSelectedCipher] = useState('englishOrdinal');
  const [showPicker, setShowPicker] = useState(false);

  const cipherOptions = useMemo(() => getCipherOptions(), []);
  const currentCipher = ciphers[selectedCipher];

  // Build letter-value pairs
  const letterValues = useMemo(() => {
    if (!currentCipher || !currentCipher.values) return [];

    return Object.entries(currentCipher.values)
      .filter(([letter]) => /^[a-z]$/i.test(letter))
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([letter, value]) => ({
        letter: letter.toUpperCase(),
        value,
      }));
  }, [selectedCipher]);

  return (
    <View style={styles.container}>
      <Text style={styles.pageTitle}>🔤 Cipher Chart</Text>
      <Text style={styles.subtitle}>Visual alphabet reference for any cipher system</Text>

      {/* Cipher Selector */}
      <TouchableOpacity
        style={styles.selector}
        onPress={() => setShowPicker(!showPicker)}
      >
        <Text style={styles.selectorLabel}>Cipher:</Text>
        <Text style={styles.selectorValue}>{currentCipher?.name || 'Select'}</Text>
        <Text style={styles.selectorArrow}>{showPicker ? '▲' : '▼'}</Text>
      </TouchableOpacity>

      {/* Picker Dropdown */}
      {showPicker && (
        <ScrollView style={styles.pickerDropdown} nestedScrollEnabled>
          {cipherOptions.map((option) => (
            <TouchableOpacity
              key={option.key}
              style={[
                styles.pickerItem,
                selectedCipher === option.key && styles.pickerItemActive,
              ]}
              onPress={() => {
                setSelectedCipher(option.key);
                setShowPicker(false);
              }}
            >
              <Text style={[
                styles.pickerItemText,
                selectedCipher === option.key && styles.pickerItemTextActive,
              ]}>
                {option.name}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      )}

      {/* Grid */}
      <View style={styles.grid}>
        {letterValues.map(({ letter, value }) => (
          <View key={letter} style={styles.tile}>
            <Text style={styles.tileLetter}>{letter}</Text>
            <Text style={styles.tileValue}>{value}</Text>
          </View>
        ))}
      </View>

      {/* Description */}
      {currentCipher?.description && (
        <View style={styles.descriptionBox}>
          <Text style={styles.descriptionText}>ℹ️ {currentCipher.description}</Text>
        </View>
      )}
    </View>
  );
};

const createStyles = (colors) => StyleSheet.create({
  container: {
    padding: 20,
    maxWidth: 700,
    alignSelf: 'center',
    width: '100%',
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: colors.textSecondary,
    marginBottom: 20,
  },
  selector: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    padding: 14,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    marginBottom: 8,
  },
  selectorLabel: {
    fontSize: 14,
    color: colors.textSecondary,
    marginRight: 8,
  },
  selectorValue: {
    flex: 1,
    fontSize: 15,
    fontWeight: '600',
    color: colors.text,
  },
  selectorArrow: {
    fontSize: 12,
    color: colors.textSecondary,
  },
  pickerDropdown: {
    maxHeight: 250,
    backgroundColor: colors.surfaceElevated,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    marginBottom: 16,
  },
  pickerItem: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.divider,
  },
  pickerItemActive: {
    backgroundColor: colors.primarySurface,
  },
  pickerItemText: {
    fontSize: 14,
    color: colors.text,
  },
  pickerItemTextActive: {
    color: colors.primary,
    fontWeight: '600',
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 10,
    marginTop: 16,
    marginBottom: 20,
  },
  tile: {
    width: 60,
    height: 70,
    backgroundColor: colors.surfaceElevated,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.border,
    shadowColor: colors.shadow,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
  },
  tileLetter: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.text,
    marginBottom: 2,
  },
  tileValue: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.primary,
  },
  descriptionBox: {
    backgroundColor: colors.primarySurface,
    padding: 14,
    borderRadius: 8,
    marginTop: 8,
  },
  descriptionText: {
    fontSize: 13,
    color: colors.textSecondary,
    lineHeight: 18,
  },
});

export default CipherChartPage;
